<template>
  <div id="loan-details-page" class="viewport viewport-with-top-nav viewport-compact">

    <TopNav></TopNav>

    <div class="display-1 pt-3 mb-0">{{ $t('LoanDetailsPage.title') }}</div>

    <div v-if="loan == null">

      <span class="skeleton-item h6 mb-3">Reference number</span><br>
      <div class="card card-shadow card-rounded border-0 mb-4">
        <div class="card-body">
          <div class="row">
            <div class="col-6 form-group">
              <span class="skeleton-item small">Short label</span><br>
              <span class="skeleton-item">Slightly longer </span><br>
              <span class="skeleton-item small">Short one </span><br>
            </div>
            <div class="col-6 form-group">
              <span class="skeleton-item small">Short label</span><br>
              <span class="skeleton-item">Short label</span>
            </div>
          </div>
          <div class="skeleton-item btn d-flex" style="line-height: inherit">Button label</div>
        </div>
      </div>

    </div>
    <template v-else>
      <div id="loan-reference" class="h6 mb-3 text-muted">{{$t('LoanDetailsPage.referenceNo', {referenceNo: loan.reference})}}</div>
      <div v-if="!isShownFullDetailsForWrittenOff && loan.subStatus === 'WRITTEN_OFF'" class="mb-4">
        <LoanDefaultedMessage :status="loan.subStatus"></LoanDefaultedMessage>
      </div>
      <DefaultedLoanCard v-if="loan.status === 'DEFAULTED'"
                         :loan="loan"
                         :showFullDetailsForWrittenOff="isShownFullDetailsForWrittenOff"/>
      <DelayedLoanCard v-else-if="loan.status === 'DELAYED'" :loan="loan"/>
      <DelayedLoanCard v-else-if="loan.status === 'NORMAL_UNPAID'" :loan="loan"/>
      <NormalLoanCard v-else-if="isNormal" :loan="loan" />

      <template v-if="isShownFullDetailsForWrittenOff || loan.subStatus !== 'WRITTEN_OFF'">
        <div class="row" v-if="loan.nextPaymentDate">
          <div class="col-6 form-group mb-2" id="loan-paid-amount">
            <div id="loan-paid-amount" class="small text-muted">{{$t('LoanDetailsPage.paidAmount')}}</div>
            <div class="text-amount">{{ $n(loan.paidAmount, 'currency') }}</div>
          </div>
          <div class="col-6 form-group mb-2 text-right" id="loan-remaining-balance">
            <div id="loan-remaining-balance" class="small text-muted">{{$t('LoanDetailsPage.remainingBalance')}}</div>
            <div class="text-amount">{{ $n(loan.remainingAmount, 'currency') }}</div>
          </div>
        </div>

        <div class="progress form-group" v-if="loan.nextPaymentDate">
          <div class="progress-bar bg-success" role="progressbar"
               :style="{ width: loanBarWidth(loan.paidAmount, loan.totalDebt) + '%' }"
               aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"></div>
        </div>
      </template>

      <LoanGeneralDetails :loan="loan" :showEir="isShownEir" :showApr="isShownApr"
                          :showFullDetailsForWrittenOff="isShownFullDetailsForWrittenOff"/>

      <div class="list-group list-group-escape-container mb-5">
        <template v-if="loan.hasPaymentSchedule">
          <router-link :to="{ name: 'loan-payment-schedule' }"
                       id="link-payment-schedule"
              class="list-group-item list-group-item-action list-group-item-with-icon">
            <span>{{ $t('LoanDetailsPage.paymentSchedule') }}</span>
            <Icon icon-name="arrow-ios-forward-outline" icon-class="text-muted-more"></Icon>
          </router-link>
          <router-link :to="{name: 'loan-full-repayment', params: {reference: loan.reference, loanEndDate: loan.endDate}}"
                       id="link-pay-loan-earlier"
                       class="list-group-item list-group-item-action list-group-item-with-icon"
                       v-if="isNormal || isDelayed">
            <span>{{ $t('LoanDetailsPage.loanRepayEarlier') }}</span>
            <Icon icon-name="arrow-ios-forward-outline" icon-class="text-muted-more"></Icon>
          </router-link>
          <router-link :to="{ name: 'loan-request-suspension', params: { reference: loan.reference } }"
                       id="link-suspend-installment"
                       class="list-group-item list-group-item-action list-group-item-with-icon"
                       v-if="isNormal || isDelayed">
            <span>{{ $t('LoanDetailsPage.suspendInstallment') }}</span>
            <Icon icon-name="arrow-ios-forward-outline" icon-class="text-muted-more"></Icon>
          </router-link>
        </template>
        <template v-if="isClosed && !$configuration.newCoreEnabled">
          <router-link :to="{name: 'loan-closure-confirmation'}"
                       id="link-closure-confirmation"
                       class="list-group-item list-group-item-action list-group-item-with-icon closure-certificate-link">
            <span>{{ $t('LoanDetailsPage.closureCertificate') }}</span>
            <Icon icon-name="arrow-ios-forward-outline" icon-class="text-muted-more"></Icon>
          </router-link>
        </template>
      </div>

      <h6 class="card-title mb-3">{{$t('LoanDetailsPage.documents')}}</h6>
      <div class="mb-5">
        <LoanTermsList>
          <template v-slot:before>
            <router-link v-if="isWebApp"
                         id="loan-contract-link"
                         :to="receiveContractRoute"
                         class="list-group-item list-group-item-action list-group-item-with-icon">
              <span>{{  $t('LoanDetailsPage.contract') }}</span>
              <Icon icon-name="arrow-ios-forward-outline" icon-class="text-muted-more"></Icon>
            </router-link>
          </template>
        </LoanTermsList>
      </div>

    </template>
    <div class="flex-grow-1"></div><!-- Fill the rest of the page-->
  </div>
</template>

<script>
import TopNav from '@shared/components/TopNav'
import Icon from '@shared/components/Icon'
import LoanTermsList from '@shared/components/LoanTerms'
import LoanDefaultedMessage from '@shared/components/loan/details/LoanDefaultedMessage'
import DefaultedLoanCard from '@shared/components/loan/details/DefaultedLoanCard'
import NormalLoanCard from '@shared/components/loan/details/NormalLoanCard'
import LoanGeneralDetails from '@shared/components/loan/details/LoanGeneralDetails'
import DelayedLoanCard from '@shared/components/loan/details/DelayedLoanCard'
import { WEBAPP } from '@shared/constants'
import settings from '@shared/settings'
import store from '@shared/store'

export default {
    name: 'LoanDetailsPage',
    components: {
      LoanDefaultedMessage,
      DefaultedLoanCard,
      DelayedLoanCard,
      NormalLoanCard,
      LoanGeneralDetails,
      LoanTermsList,
      Icon,
      TopNav
    },
    props: ['showFullDetailsForWrittenOff', 'showEir', 'showApr'],
    data () {
      return {
        loan: null
      }
    },
    created() {
      this.getLoanById(this.$route.params.id)
      if (store.needsToShowPaymentScheduleSurvey()) {
        store.resetShowPaymentScheduleSurvey()
        if (settings.featureFlags.isSurveyEnabled()) {
          this.$router.push({
            name: 'loan-payment-schedule-survey'
          })
        }
      }
    },
    computed: {
      isWebApp() {
        return this.loan.signingLocation === WEBAPP
      },
      isShownFullDetailsForWrittenOff() {
        return !!this.showFullDetailsForWrittenOff
      },
      isShownEir() {
        return this.showEir === undefined ? true : this.showEir
      },
      isShownApr() {
        return this.showApr === undefined ? true : this.showApr
      },
      isClosed() {
        return this.loan.status === 'CLOSED'
      },
      isNormal() {
        return this.loan.status === 'NORMAL' || this.loan.status === 'NORMAL_UNPAID'
      },
      isDelayed() {
        return this.loan.status === 'DELAYED'
      },
      receiveContractRoute() {
        return { name: 'receive-loan-contract-signed-and-paid-out', params: { reference: this.loan.reference } }
      }
    },
    methods: {
      getLoanById(loanId) {
        this.$http.get(`/api/loans/${loanId}/`).then(response => {
          this.loan = response.data.loan
        })
      },
      loanBarWidth: function (paid, totalDebt) {
        return (paid / totalDebt) * 100
      }
    }
  }
</script>
